<template>
  <div v-if="shouldShowButton" class="google-translate">
    <button
      v-if="isShowingOriginal || isTranslating"
      :disabled="isTranslating"
      class="button outline square minimal sml"
      @click.prevent="showContent('translated')"
    >
      <img
        class="icon"
        src="@/assets/images/google-g.svg"
        width="24"
        height="24"
        alt=""
      />
      <translate>Translate</translate>
    </button>
    <button
      v-else-if="isShowingTranslated"
      class="button outline square minimal sml"
      @click.prevent="showContent('original')"
    >
      <img
        class="icon"
        src="@/assets/images/google-g.svg"
        width="24"
        height="24"
        alt=""
      />
      <translate>Show original</translate>
    </button>
    <a href="https://translate.google.com/" target="_blank" rel="noopener">
      <img
        src="@/assets/images/powered-by-google-translate.png"
        width="180"
        height="16"
        :alt="$t('Powered by Google Translate').toString()"
      />
    </a>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Setup,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import {
  GoogleTranslateState,
  googleTranslateService,
} from '../googleTranslateMachine'

@Component
class GoogleTranslateButton extends Vue {
  @Prop({ required: false })
  parent?: Element

  @Prop({ default: '' })
  querySelectorValue: string

  @Prop({ default: 'html' })
  format: 'text' | 'html'

  shouldShowButton = false

  @Setup(() => googleTranslateService())
  private googleTranslateMachine: GoogleTranslateState | null = null

  mounted() {
    this.shouldShowButton = true
  }

  private nodes: Element[] = []
  currentStateValue: string = 'showingOriginal' // initial state from machine

  get isShowingOriginal() {
    return this.currentStateValue === 'showingOriginal'
  }

  get isShowingTranslated() {
    return this.currentStateValue === 'showingTranslated'
  }

  get isTranslating() {
    return this.currentStateValue === 'translating'
  }

  showContent(contentType: 'translated' | 'original') {
    this.ensureDataInitialized()
    contentType === 'translated' ? this.translate() : this.showOriginal()
  }

  private ensureDataInitialized() {
    if (!this.googleTranslateMachine) {
      return
    }
    if (this.googleTranslateMachine?.initialized) {
      return
    }
    this.nodes = this.getTranslatableNodes()
    this.googleTranslateMachine
      .onTransition((state) => {
        this.currentStateValue = state.value
        if (state.matches('showingTranslated')) {
          this.overrideTranslatableNodesWithTexts(state.context.translatedTexts)
        } else if (state.changed && state.matches('showingOriginal')) {
          this.overrideTranslatableNodesWithTexts(state.context.originalTexts)
        }
      })
      .start()
  }

  private getTranslatableNodes() {
    const nodes = this.parent
      ? this.parent.querySelectorAll('p,span,li')
      : document.querySelectorAll(
          `[data-g-translate="${this.querySelectorValue}"]`,
        )
    return Array.from(nodes)
  }

  private translate() {
    this.googleTranslateMachine.send('TRANSLATE', {
      format: this.format,
      texts: this.nodes.map((n) => n.innerHTML),
      targetLang: this.$i18n.locale,
    })
  }

  private showOriginal() {
    this.googleTranslateMachine.send('SHOW_ORIGINAL')
  }

  private overrideTranslatableNodesWithTexts(texts: string[]) {
    for (const [index, _c] of this.nodes.entries()) {
      this.nodes[index].innerHTML = texts[index]
    }
  }
}

export default toNative(GoogleTranslateButton)
</script>
<style scoped>
div,
button {
  display: inline-flex;
  align-items: center;
}

.google-translate {
  flex-wrap: wrap;
  gap: 1em;
  margin: 0.5em 0;
  row-gap: 0.25em;
}

.icon {
  margin: -0.3em 0.4em -0.3em -0.1em;
}
</style>
